import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { siteConfig } from '@/constants/site'

interface Props extends SeoProps {
    origin?: string
    path?: string
}

const absolute = (origin: string | undefined, value: string | undefined): string | undefined => {
    if (!value) return undefined
    if (/^https?:\/\//.test(value)) return value

    return origin ? `${origin}${value.startsWith('/') ? value : `/${value}`}` : value
}

export const Seo: FC<Props> = ({
    title,
    description = siteConfig.description,
    ogImage = siteConfig.ogImage,
    noIndex = false,
    origin,
    path
}): ReactNode => {
    const pageTitle = title ? `${title} - ${siteConfig.name}` : siteConfig.name
    const pageUrl = origin ? `${origin}${path ?? ''}` : siteConfig.url
    const image = absolute(origin, ogImage)

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={description} />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={description} />
            <meta property='og:type' content='website' />
            <meta property='og:site_name' content={siteConfig.name} />
            {pageUrl && <meta property='og:url' content={pageUrl} />}
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={description} />

            {image && <meta property='og:image' content={image} />}
            {image && <meta name='twitter:image' content={image} />}
            {noIndex && <meta name='robots' content='noindex, nofollow' />}
        </Head>
    )
}
